import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
// import { selectors } from '../../store/selectors/shipBuilderSelectors'; // Not used in this file
import * as derivedSelectors from '../../store/selectors/derivedSelectors';

/**
 * Centralized power calculation that uses existing selectors and footer calculations
 * as single sources of truth to ensure consistency across the application
 */

// Selector for basic systems power (20% of hull tonnage)
export const selectBasicSystemsPower = createSelector(
  [(state: RootState) => state.shipBuilder.shipSize],
  (shipSize) => (shipSize || 0) * 0.2
);

// Selector for bridge power
export const selectBridgePower = createSelector(
  [(state: RootState) => state.shipBuilder.bridge],
  (bridge) => bridge?.power || 0
);

// Selector for sensors power
export const selectSensorsPower = createSelector(
  [(state: RootState) => state.shipBuilder.sensors],
  (sensors) => sensors?.power || 0
);

// Selector for stateroom power (using same calculation as PowerPlantSelector)
export const selectStateroomPower = createSelector(
  [(state: RootState) => state.shipBuilder.stateroomConfig],
  (stateroomConfig) => {
    if (!stateroomConfig) return 0;
    
    return (
      // Staterooms power (0 for standard)
      (stateroomConfig.single || 0) * 0 + // Standard staterooms have 0 power
      (stateroomConfig.double || 0) * 0 + // Standard staterooms have 0 power
      // Low berths power (1 per 10 berths)
      Math.ceil((stateroomConfig.lowBerths?.count || 0) / 10) +
      // Emergency low berths power
      (stateroomConfig.emergencyLowBerths?.count || 0) * 1 + // 1 power each
      // Common area power
      (stateroomConfig.commonArea || 0) * 0.5 // 0.5 power per ton
    );
  }
);

// Selector for weapons power (using same calculation as WeaponsFooterSummary)
export const selectWeaponsPower = createSelector(
  [(state: RootState) => state.shipBuilder.weaponMounts],
  (weaponMounts) => {
    if (!weaponMounts) return 0;
    
    let totalWeaponPower = 0;
    weaponMounts.forEach((mount) => {
      mount.weapons.forEach((weapon) => {
        totalWeaponPower += weapon.power || 0;
      });
    });
    return totalWeaponPower;
  }
);

// Selector for defensive systems power (using same calculation as WeaponsFooterSummary)
export const selectDefensiveSystemsPower = createSelector(
  [(state: RootState) => state.shipBuilder.defensiveSystems],
  (defensiveSystems) => {
    if (!defensiveSystems) return 0;
    
    return defensiveSystems.reduce((sum, sys) => {
      const quantity = sys.quantity || 1;
      return sum + ((sys.power || 0) * quantity);
    }, 0);
  }
);

// Selector for optional systems power (using existing selector)
export const selectOptionalSystemsPower = createSelector(
  [(state: RootState) => state.optionalSystems.systems],
  (systems) => {
    if (!systems) return 0;
    
    return systems.reduce((total: number, system) => {
      return total + (system.power || 0);
    }, 0);
  }
);

// Centralized power requirements calculation
export const selectCentralizedPowerRequirements = createSelector(
  [
    selectBasicSystemsPower,
    derivedSelectors.selectTotalDrivePower,
    selectBridgePower,
    selectSensorsPower,
    selectWeaponsPower,
    selectDefensiveSystemsPower,
    selectOptionalSystemsPower,
    selectStateroomPower,
  ],
  (
    basicSystemsPower,
    totalDrivePower,
    bridgePower,
    sensorsPower,
    weaponsPower,
    defensiveSystemsPower,
    optionalSystemsPower,
    stateroomPower
  ) => {
    // Calculate drive power breakdown
    const maneuverDrivePower = totalDrivePower * 0.5; // Assuming 50/50 split for now
    const jumpDrivePower = totalDrivePower * 0.5;
    
    // Calculate additional power (everything except basic systems and drives)
    const additionalPower = 
      bridgePower +
      sensorsPower +
      weaponsPower +
      defensiveSystemsPower +
      optionalSystemsPower +
      stateroomPower;
    
    // Calculate operational requirements
    const normalOperations = basicSystemsPower + maneuverDrivePower + additionalPower;
    const jumpOperations = basicSystemsPower + jumpDrivePower + additionalPower;
    const maximumLoad = basicSystemsPower + totalDrivePower + additionalPower;
    
    return {
      basicSystems: basicSystemsPower,
      maneuverDrive: maneuverDrivePower,
      jumpDrive: jumpDrivePower,
      bridge: bridgePower,
      sensors: sensorsPower,
      weapons: weaponsPower,
      defensiveSystems: defensiveSystemsPower,
      optionalSystems: optionalSystemsPower,
      staterooms: stateroomPower,
      additionalPower,
      total: maximumLoad,
      normalOperations,
      jumpOperations,
      maximumLoad,
    };
  }
);

// Debug selector to help identify discrepancies
export const selectPowerCalculationDebug = createSelector(
  [
    selectBasicSystemsPower,
    derivedSelectors.selectTotalDrivePower,
    selectBridgePower,
    selectSensorsPower,
    selectWeaponsPower,
    selectDefensiveSystemsPower,
    selectOptionalSystemsPower,
    selectStateroomPower,
  ],
  (
    basicSystemsPower,
    totalDrivePower,
    bridgePower,
    sensorsPower,
    weaponsPower,
    defensiveSystemsPower,
    optionalSystemsPower,
    stateroomPower
  ) => {
    const additionalPower = 
      bridgePower +
      sensorsPower +
      weaponsPower +
      defensiveSystemsPower +
      optionalSystemsPower +
      stateroomPower;
    
    return {
      breakdown: {
        basicSystems: basicSystemsPower,
        totalDrivePower,
        bridge: bridgePower,
        sensors: sensorsPower,
        weapons: weaponsPower,
        defensiveSystems: defensiveSystemsPower,
        optionalSystems: optionalSystemsPower,
        staterooms: stateroomPower,
        additionalPower,
      },
      total: basicSystemsPower + totalDrivePower + additionalPower,
    };
  }
);
